import Image from 'next/image'
import { useState } from 'react'

import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Badge } from '@wartek-id/badge'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import { PENGELOLAAN_KINERJA_KS_PENILAI_PATH } from 'app/KinerjaShared/constants/path'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { DialogPltKSConfirmation } from '../DialogPltKSConfirmation'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { CardKSSchool } from 'app/KinerjaShared/components/CardKSSchool'

import type { KinerjaHeadmasterCommonRoleType } from 'types/kinerja'
import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

interface CardKinerjaPeriksaMultipleSchoolProps {
  isLoading?: boolean
  title?: string
  subtitle?: string
  badgeText?: string
  schools: PelaksanaanSchool[]
  baseUrl?: string
  roleType?: KinerjaHeadmasterCommonRoleType
}

export default function CardKinerjaPeriksaMultipleSchool({
  isLoading = false,
  schools,
  title = 'Periksa Perencanaan Kinerja',
  subtitle = 'Mohon cek dan setujui. Anda juga bisa memberikan penyesuaian jika diperlukan.',
  badgeText = 'Khusus Kepala Sekolah',
  baseUrl = PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
  roleType = 'KS',
}: CardKinerjaPeriksaMultipleSchoolProps) {
  const { periodNo } = usePeriodContext()
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

  const handleShowConfirmationDialog = () => setShowConfirmationDialog(true)

  return (
    <>
      <Card className="p-6">
        <div className="flex gap-6">
          <Image alt="" src="/icon/typing-monitor.png" width={84} height={84} />
          <div className="flex-1 flex flex-col gap-1 justify-center">
            <Text
              variant="subheading"
              color="informational"
              className="!text-blue-60 !uppercase tracking-[0.5px]"
            >
              {periodNo === 1 ? '1-31 Januari' : '1-31 Juli'}
            </Text>
            <div className="flex items-center gap-2">
              <Text variant="heading-lg" as="h2">
                {title}
              </Text>
              <Badge className="uppercase whitespace-nowrap">{badgeText}</Badge>
            </div>
            <Text color="subdued">{subtitle}</Text>
          </div>
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(400px,1fr))] pt-6 gap-6">
          {isLoading ? (
            <>
              <Shimmer height="120px" width="100%" />
              <Shimmer height="120px" width="100%" />
            </>
          ) : (
            schools.map((school) => {
              const nextPath =
                roleType === 'KSPNF'
                  ? `${baseUrl}/konfirmasi-profil?id=${school.npsn}`
                  : `${baseUrl}/periksa/${school.npsn}`

              return (
                <CardKSSchool
                  key={school.npsn}
                  nextText="Periksa"
                  nextPath={nextPath}
                  confirmationPath={
                    school.statusSatuanPendidikan === 'SWASTA'
                      ? `${baseUrl}/konfirmasi-swasta/${school.npsn}?successPath=${nextPath}`
                      : undefined
                  }
                  onConfirmClick={
                    school.statusSatuanPendidikan === 'NEGERI'
                      ? handleShowConfirmationDialog
                      : undefined
                  }
                  confirmationStatus={school.confirmationStatus}
                  schoolName={school.schoolName}
                  schoolStatus={school.statusSatuanPendidikan}
                  schoolRole={school.role}
                  npsn={school.npsn}
                />
              )
            })
          )}
        </div>
        <div className="rounded bg-cyan-5 p-3 mt-6 flex items-center">
          <Icon color="informational" className="mr-1">
            error
          </Icon>
          Jika satuan pendidikan tidak sesuai, mohon kunjungi&nbsp;
          <a
            className="!text-blue-70 underline"
            href={PANDUAN_PENGELOLAAN_URL}
            target="_blank"
            rel="noreferrer"
          >
            Pusat Bantuan
          </a>
          .
        </div>
      </Card>

      <DialogPltKSConfirmation
        isOpen={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
      />
    </>
  )
}
